import { useState, useEffect, useRef } from "react";
import { useQuery } from "@apollo/client";
import { isEqual, get } from "lodash";
import { useSelector } from "react-redux";
import { RaceProgram } from "@tvg/ts-types/Race";
import { useDeepEffect, useWagerProfile } from "@tvg/custom-hooks";
// @ts-ignore version of react-router-dom get this error
import { useNavigate } from "react-router-dom";
import groupByWagerType from "@tvg/desktop-bet/src/utils/groupByWagerType";
import { getIsLogged } from "@urp/store-selectors";
import useTrackInfo from "./useTrackInfo";
import useRacesSubscription from "./useRacesSubscription";
import queryHelper from "../graphql/options.graph";
import { GET_PROGRAM_PAGE_RACE } from "../graphql/queries/RaceProgramQuery";

const getCurrentRace = (races: RaceProgram[], currentRaceId: string) =>
  races.find((race: RaceProgram) => race.id === currentRaceId);

const useCurrentRaces = () => {
  const isLogged = useSelector(getIsLogged);
  const [currentRace, setCurrentRace] = useState<RaceProgram>({});
  const lastLoadingValue = useRef(false);
  const lastLoginValue = useRef(false);
  const navigate = useNavigate();
  const wagerProfile: string = useWagerProfile();
  const { track, raceNumber } = useTrackInfo();

  // request races data
  const { data, previousData, loading, subscribeToMore } = useQuery(
    GET_PROGRAM_PAGE_RACE,
    queryHelper.apolloOptions({ track, wagerProfile })
  );

  // calculate current race id
  const currentRaceId = `${track}-${raceNumber}`;
  // select races to load
  const races = queryHelper.getRaces(loading ? previousData : data) || [];

  // subscribe for changes
  const racesIds = races.map((race: RaceProgram) => race.tvgRaceId);
  useRacesSubscription(wagerProfile, racesIds, subscribeToMore);

  // select current race
  const searchCurrentRace = getCurrentRace(races, currentRaceId);

  if (searchCurrentRace && searchCurrentRace.wagerTypes) {
    searchCurrentRace.wagerTypes = groupByWagerType(
      searchCurrentRace.wagerTypes
    ).sort((a, b) => a.type.id - b.type.id);
  }

  useDeepEffect(() => {
    if (searchCurrentRace && searchCurrentRace !== currentRace) {
      setCurrentRace(searchCurrentRace);
    }
  }, [searchCurrentRace]);

  // save old data when new one is not available
  useDeepEffect(() => {
    const foundCurrentRace = getCurrentRace(races, currentRaceId);

    if (foundCurrentRace && foundCurrentRace.wagerTypes) {
      foundCurrentRace.wagerTypes = groupByWagerType(
        foundCurrentRace.wagerTypes
      ).sort((a, b) => a.type.id - b.type.id);
    }

    if (foundCurrentRace) {
      setCurrentRace(foundCurrentRace);
    }
  }, [data]);

  useEffect(() => {
    const previousRaces = get(previousData, "races", []);
    const isInvalidUserRace =
      isLogged &&
      !isEqual(previousRaces, races) &&
      previousRaces.length > 0 &&
      races.length === 0;

    if (
      (lastLoadingValue.current &&
        !loading &&
        ((races.length === 0 && isEqual(previousRaces, races)) ||
          (races.length > 0 &&
            currentRaceId.startsWith(get(races[0], "track.trackCode")) &&
            !getCurrentRace(races, currentRaceId)))) ||
      isInvalidUserRace
    ) {
      navigate("/");
    }
    lastLoginValue.current = isLogged;
    lastLoadingValue.current = loading;
  }, [loading]);

  return {
    races,
    currentRace: searchCurrentRace || currentRace,
    loading: loading && races.length > 0
  };
};

export default useCurrentRaces;
